import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRightOutlined } from "@/config/antd";
import {
  updateStep,
  setCurrentStep,
  selectSubEventStep1,
} from "@/store/redux/slices/event/sub_event_slice";
import Back from "@/components/specific/Back";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import MemberPicker from "@/components/specific/Member_Picker";
import CustomScheduleInput from "@/components/specific/Custom_Schedule_Input";
// import VVCSetupDialog from "@/ui/VVC_Setup_Dialog";
import InviteMembersDialog from "@/ui/Invite_Members";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  getAllManifestos,
  selectManifestos,
} from "@/store/redux/slices/manifesto/manifesto_slice";
import { getAccessToken } from "@/utils/token_functions";
import DOMPurify from "dompurify";
import LocationInput from "@/components/common/inputs/Location_Input";
import WebIcon from "@/components/common/Web_Icons";
// import Calendar from "@/components/specific/Calender";
// import TimeRangeInput from "@/components/specific/Time_Range_Input";

const Step1AddSubEvent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { eventId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const step1Data = useSelector(selectSubEventStep1);
  const manifestos = useSelector(selectManifestos);

  const handleManifestoSelectClick = () => {
    if (manifestos.length === 0) {
      dispatch(getAllManifestos());
    }
  };

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      dispatch(getAllManifestos());
    }
  }, [dispatch]);

  const manifestoOptions = manifestos.map((manifesto) => ({
    value: manifesto._id,
    label: manifesto.name,
  }));

  // Replace individual state variables with formData
  const [formData, setFormData] = useState({
    name: step1Data.name || "",
    description: step1Data.description || "",
    eventType: step1Data.eventType || "",
    duration: {
      startDate: step1Data.duration?.startDate || "",
      startTime: step1Data.duration?.startTime || "",
      endDate: step1Data.duration?.endDate || "",
      endTime: step1Data.duration?.endTime || "",
    },
    location: step1Data.location || {
      type: "",
      address: "",
      virtualService: "",
      url: "",
    },
    members: step1Data.members || [],
    manifestoID: step1Data.manifestoID || "",
    manifestoType: step1Data.manifestoType || "",
  });

  // Add separate state for custom date/time inputs
  const [customSchedule, setCustomSchedule] = useState({
    customDate: "",
    customTimeFrom: "",
    customTimeTo: "",
  });

  // Update form handlers
  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleLocationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value,
      },
    }));
  };

  const handleMembersSelected = (newMembers) => {
    setFormData((prev) => ({
      ...prev,
      members: [
        ...prev.members,
        ...newMembers.filter(
          (member) =>
            !prev.members.some((existing) => existing.id === member.id)
        ),
      ],
    }));
  };

  const handleDeleteMember = (member) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.filter((m) => m.id !== member.id),
    }));
  };

  const handleDeleteConfirm = () => {
    showLoader();

    setTimeout(() => {
      hideLoader();
      setFormData((prev) => ({
        ...prev,
        members: prev.members.filter(
          (m) => m.id !== formData.members.find((m) => m.id === m.id)
        ),
      }));
    }, 2000);
  };

  const handleUpdateMember = (memberId, newRole) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.map((member) =>
        member.id === memberId ? { ...member, role: newRole } : member
      ),
    }));
  };

  const handleScheduleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      duration: {
        ...prev.duration,
        type: value,
      },
    }));

    // Clear custom fields if not using custom duration
    if (value !== "custom") {
      setCustomSchedule({
        customDate: "",
        customTimeFrom: "",
        customTimeTo: "",
      });
    }
  };

  const handleCustomDateChange = (dateRange) => {
    setCustomSchedule((prev) => ({
      ...prev,
      customDate: dateRange,
    }));
  };

  const handleCustomTimeFromChange = (time) => {
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setCustomSchedule((prev) => ({
      ...prev,
      customTimeFrom: formattedTime,
    }));
  };

  const handleCustomTimeToChange = (time) => {
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setCustomSchedule((prev) => ({
      ...prev,
      customTimeTo: formattedTime,
    }));
  };

  const eventTypeOptions = [
    { value: "physical", label: "Physical Event" },
    { value: "virtual", label: "Virtual Event" },
    { value: "physical_and_virtual", label: "Physical and Virtual Event" },
  ];

  const handleBack = () => {
    return `/dashboard/events/${eventId}`;
  };

  const handleProceed = () => {
    showLoader();
    try {
      let startDate, endDate, startTime, endTime;

      if (formData.duration.type === "custom") {
        // Handle custom date/time
        const [startDateStr, endDateStr] =
          customSchedule.customDate.split(" - ");

        // Convert date strings to proper format (YYYY-MM-DD)
        const formatDate = (dateStr) => {
          const date = new Date(dateStr);
          return date.toISOString().split("T")[0];
        };

        startDate = formatDate(startDateStr);
        endDate = formatDate(endDateStr);
        startTime = customSchedule.customTimeFrom;
        endTime = customSchedule.customTimeTo;
      } else {
        // Handle fixed duration (24, 48 hours etc)
        const now = new Date();
        startDate = now.toISOString().split("T")[0];

        const hours = parseInt(formData.duration.type);
        const endDateTime = new Date(now.getTime() + hours * 60 * 60 * 1000);
        endDate = endDateTime.toISOString().split("T")[0];

        // Set default times for non-custom durations
        startTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        endTime = endDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      // Sanitize description
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }).trim();

      const step1Data = {
        name: formData.name,
        description: sanitizedDescription,
        eventType: formData.eventType,
        duration: {
          startDate,
          startTime,
          endDate,
          endTime,
        },
        location: {
          type: formData.eventType,
          address: formData.location.address,
          url: formData.location.url,
        },
        members: formData.members.map((member) => member.id),
        manifestoID: formData.manifestoType,
      };

      dispatch(
        updateStep({
          step: "step1",
          data: {
            ...step1Data,
            // Keep full member objects in Redux for UI display
            members: formData.members,
          },
        })
      );

      dispatch(setCurrentStep(2));

      setTimeout(() => {
        hideLoader();
        navigate(`/dashboard/events/${eventId}/add-sub-event/step-2`);
      }, 2000);
    } catch (error) {
      console.error("Failed to process step 1:", error);
      hideLoader();
    }
  };

  const isFormValid =
    formData.name &&
    formData.description &&
    formData.eventType &&
    ((formData.eventType === "physical" && formData.location.address) ||
      (formData.eventType === "virtual" &&
        formData.location.virtualService &&
        formData.location.url) ||
      (formData.eventType === "physical_and_virtual" &&
        formData.location.address &&
        formData.location.virtualService &&
        formData.location.url)) &&
    formData.members.length > 0 &&
    formData.manifestoType &&
    formData.duration.type &&
    (formData.duration.type !== "custom" ||
      (customSchedule.customDate &&
        customSchedule.customTimeFrom &&
        customSchedule.customTimeTo));

  // Roles
  const roleOptions = [
    { value: "speaker", label: "Speaker" },
    { value: "coordinator", label: "Coordinator" },
    { value: "participant", label: "Participant" },
  ];

  // Add virtualServiceOptions
  const virtualServiceOptions = [
    { value: "third_party", label: "Third Party Service" },
    { value: "vvc", label: "Veegil Virtual Conference (VVC)" },
  ];

  const durationOptions = [
    { value: "24", label: "24 Hours" },
    { value: "48", label: "48 Hours" },
    {
      value: "custom",
      label: "Custom",
      customOptions: [
        { value: "72", label: "72 Hours" },
        { value: "96", label: "96 Hours" },
        { value: "120", label: "120 Hours" },
      ],
    },
  ];

  return (
    <div className="w-full my-5 text-left">
      <Back text="Back" onClick={handleBack} />

      <div className="lg:p-5 my-5 rounded-lg lg:border">
        <h1 className="mb-2 font-semibold heading-medium text-darkGrey">
          Setup your Sub Event
        </h1>
        <p className="mb-8 text-small text-darkGrey">
          Fill the form below. All fields are required to be filled with
          accurate information.
        </p>

        <div className="space-y-6">
          {/* Basic Info Section */}
          <div>
            <h2 className="mb-4 font-semibold text-bold text-darkGrey">
              Your Sub-Event&apos;s information can be inputted right at this
              section.
            </h2>
            <div className="space-y-4">
              <Input
                label="NAME"
                placeholder="Enter sub-event name"
                value={formData.name}
                onChange={handleInputChange("name")}
              />
              <TextEditor
                label="DESCRIPTION"
                placeholder="Enter description"
                value={formData.description}
                onChange={handleEditorChange}
              />
            </div>
          </div>

          {/* Location Section */}
          <div className="space-y-4">
            <h2 className="mb-2 font-semibold text-bold text-darkGrey">
              Sub-Event&apos;s Location
            </h2>
            <p className="mb-4 text-small text-darkGrey">
              This is exactly where the event is going to take place. It could
              be physical or virtual or both at the same time.
            </p>
            <Input
              type="select"
              label="LOCATION TYPE"
              placeholder="Select"
              value={formData.eventType}
              onChange={(e) => {
                handleInputChange("eventType")(e);
                // Reset other fields when location type changes
                handleLocationChange("address", "");
                // handleLocationChange('virtualService', '');
                handleLocationChange("url", "");
              }}
              options={eventTypeOptions}
            />

            {/* Physical Location Input */}
            {(formData.eventType === "physical" ||
              formData.eventType === "physical_and_virtual") && (
              <LocationInput
                label="LOCATION ADDRESS"
                placeholder="Enter location"
                value={formData.location.address}
                onChange={(e) =>
                  handleLocationChange("address", e.target.value)
                }
              />
            )}

            {/* Virtual Location Inputs */}
            {(formData.eventType === "virtual" ||
              formData.eventType === "physical_and_virtual") && (
              <div className="space-y-4">
                <Input
                  type="select"
                  label="VIRTUAL MEETING SERVICE"
                  placeholder="Select"
                  value={formData.location.virtualService}
                  onChange={(e) => {
                    handleLocationChange("virtualService", e.target.value);
                    handleLocationChange("url", "");
                  }}
                  options={virtualServiceOptions}
                />

                {formData.location.virtualService === "third_party" && (
                  <Input
                    label="ENTER VIRTUAL MEETING URL"
                    placeholder="EDIT"
                    value={formData.location.url}
                    onChange={(e) =>
                      handleLocationChange("url", e.target.value)
                    }
                    icon={<WebIcon icon="VideoPlayer" size="small" />}
                  />
                )}

                {/* {formData.location.virtualService === 'vvc' && (
                  <div className="space-y-2">
                    <Input
                      label="ENTER EXISTING VVC LINK"
                      icon={<VideoCameraOutlined />}
                      placeholder="Edit"
                      value={formData.location.url}
                      onChange={(e) => handleLocationChange('url', e.target.value)}
                    />

                    {vvcSchedule && (
                      <div className="mt-4">
                        <h3 className="mb-2 font-semibold text-darkGrey text-regular">
                          CUSTOM CONFERENCE SCHEDULE
                        </h3>
                        <div className="text-darkGrey text-small">
                          {vvcSchedule.type === 'immediate'
                            ? 'Immediate (Same as sub-event start time)'
                            : vvcSchedule.formattedDateTime
                          }
                        </div>
                      </div>
                    )}
                    <Button
                      text="SETUP NEW VEEGIL VIRTUAL CONFERENCE"
                      icon={<PlusOutlined />}
                      onClick={() => setCustomSchedule(prev => ({ ...prev, isVVCModalVisible: true }))}
                      color="primary"
                      className="text-[9px] h-[20px] py-[15px] border border-primary"
                      customBgColor="white"
                      customTextColor="primary"
                      iconPosition="left"
                    />
                    <VVCSetupDialog
                      isVisible={customSchedule.isVVCModalVisible}
                      onClose={() => setCustomSchedule(prev => ({ ...prev, isVVCModalVisible: false }))}
                    />
                  </div>
                )} */}
              </div>
            )}
          </div>

          {/* Speakers Section */}
          <div>
            <h2 className="mb-2 font-semibold text-bold text-darkGrey">
              Sub Event Speakers
            </h2>
            <p className="mb-4 text-small text-darkGrey">
              Add images of speakers that will be present at this sub event
            </p>
            <MemberPicker
              members={formData.members}
              roles={roleOptions}
              onOpenModal={() =>
                setFormData((prev) => ({
                  ...prev,
                  isInviteMembersModalVisible: true,
                }))
              }
              onDeleteMember={handleDeleteMember}
              onUpdateMember={handleUpdateMember}
            />

            <InviteMembersDialog
              isVisible={formData.isInviteMembersModalVisible}
              onClose={() =>
                setFormData((prev) => ({
                  ...prev,
                  isInviteMembersModalVisible: false,
                }))
              }
              onMembersSelected={handleMembersSelected}
              modalTitle="Add New Speaker"
              primaryHeader="Invited Members"
              showSecondaryHeader={true}
              secondaryHeader="Other Members"
              userRoleDisplay="select"
              roleOptions={roleOptions}
              invitedMembers={formData.members}
              emptyInvitedMessage="No speakers have been invited yet."
              onUpdateMember={handleUpdateMember}
            />

            <Delete
              isVisible={formData.isDeleteModalVisible}
              onClose={() =>
                setFormData((prev) => ({
                  ...prev,
                  isDeleteModalVisible: false,
                }))
              }
              onProceed={handleDeleteConfirm}
              header={`Remove ${
                formData.members.find((m) => m.id === formData.memberToDelete)
                  ?.name
              }`}
              paragraph={`Are you sure you want to remove <span class="text-primary">${
                formData.members.find((m) => m.id === formData.memberToDelete)
                  ?.name
              }</span> from this event?`}
              proceedButtonText="Remove"
              cancelButtonText="Cancel"
            />

            <Success
              isVisible={formData.isSuccessModalVisible}
              onClose={() =>
                setFormData((prev) => ({
                  ...prev,
                  isSuccessModalVisible: false,
                }))
              }
              header="Member Removed"
              paragraph={`${
                formData.members.find((m) => m.id === formData.memberToDelete)
                  ?.name
              } has been removed from this event.`}
              buttonText="Close"
              onButtonClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  isSuccessModalVisible: false,
                }))
              }
            />
          </div>

          {/* Manifesto Section */}
          <div>
            <h2 className="mb-2 font-semibold text-bold text-darkGrey">
              Manifesto
            </h2>
            <p className="mb-4 text-small text-darkGrey">
              Select the type of manifesto for this sub event
            </p>
            <Input
              type="select"
              label="MANIFESTO TYPE"
              placeholder="Select manifesto type"
              value={formData.manifestoType}
              onChange={(e) => handleInputChange("manifestoType")(e)}
              options={manifestoOptions}
              onClick={handleManifestoSelectClick}
            />
            {manifestoOptions.length === 0 && (
              <p className="mt-2 text-small text-darkGrey">
                No manifestos yet. Create one in the Manifesto module, then come
                back to attach it.{" "}
                <Link
                  to="/dashboard/manifesto/add"
                  className="text-primary underline font-medium">
                  Go to Manifesto
                </Link>
              </p>
            )}
          </div>

          {/* Schedule Section */}
          <div className="space-y-2">
            <h2 className="text-left text-bold">Schedule</h2>
            <p className="text-left text-gray-500 text-small">
              Set up the schedule for this sub event
            </p>
            <CustomScheduleInput
              options={durationOptions}
              value={formData.duration.type}
              onChange={handleScheduleChange}
              onCustomDateChange={handleCustomDateChange}
              onCustomTimeFromChange={handleCustomTimeFromChange}
              onCustomTimeToChange={handleCustomTimeToChange}
              customDate={customSchedule.customDate}
              customTimeFrom={customSchedule.customTimeFrom}
              customTimeTo={customSchedule.customTimeTo}
            />
          </div>

          {/* Proceed Button */}
          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            disabled={!isFormValid}
            color="primary"
            className={`
              w-full mt-8
              ${!isFormValid && "bg-darkBlue text-primary cursor-not-allowed"}
            `}
            customBgColor={isFormValid ? "primary" : "darkBlue"}
            customTextColor={isFormValid ? "white" : "primary"}
          />
        </div>
      </div>
    </div>
  );
};

export default Step1AddSubEvent;
